import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, Sparkles } from "lucide-react";
import { useCart } from "@/lib/cart";
import { BRANDING } from "@/branding";
import { CATEGORY_DECK } from "@/branding";

export default function Header() {
  const { count, open } = useCart();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-3">
        {/* Left: mobile menu button + brand */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-brand hover:bg-cream-2"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-4 w-4 text-plum" />
          </button>
          <Link to="/" className="flex items-baseline gap-1 min-w-0" data-testid="brand-link">
            <Sparkles className="h-4 w-4 text-gold hidden sm:inline-block twinkle" />
            <span className="font-serif-display text-xl sm:text-2xl tracking-tight text-plum-deep truncate">
              {BRANDING.storeName}
            </span>
            {BRANDING.storeNameAccent && (
              <span className="hidden sm:inline text-sm italic text-gold">{BRANDING.storeNameAccent}</span>
            )}
          </Link>
        </div>

        {/* Center: nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm">
          <NavLink to="/shop" className={({ isActive }) => (isActive ? "text-plum-deep" : "text-plum/70 hover:text-plum-deep")} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/shop?featured=1" className="text-plum/70 hover:text-plum-deep" data-testid="nav-featured">Featured</NavLink>
          <NavLink to="/shop?new=1" className="text-plum/70 hover:text-plum-deep" data-testid="nav-new">New</NavLink>
          <NavLink to="/about" className={({ isActive }) => (isActive ? "text-plum-deep" : "text-plum/70 hover:text-plum-deep")} data-testid="nav-about">About</NavLink>
        </nav>

        {/* Right: search + cart */}
        <div className="flex items-center gap-2">
          <Link
            to="/shop"
            className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-brand hover:bg-cream-2 pill-btn text-plum"
            data-testid="search-btn"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </Link>
          <button
            onClick={open}
            className="relative h-10 px-4 inline-flex items-center gap-2 rounded-full bg-plum text-white hover:bg-plum-deep pill-btn text-sm"
            data-testid="open-cart-btn"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center text-[11px] bg-gold text-plum-deep rounded-full h-5 min-w-5 px-1 font-medium"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav overlay — portaled to body so backdrop-filter on <header> does not create a containing block */}
      {mobileNavOpen && typeof document !== "undefined" && createPortal(
        <div className="md:hidden fixed inset-0 z-[60] bg-cream overflow-y-auto" data-testid="mobile-nav">
          <div className="h-16 px-5 flex items-center justify-between border-b border-brand">
            <Link to="/" onClick={() => setMobileNavOpen(false)} className="font-serif-display text-xl text-plum-deep">{BRANDING.storeName}</Link>
            <button
              onClick={() => setMobileNavOpen(false)}
              className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-brand"
              aria-label="Close menu"
              data-testid="mobile-close-btn"
            >
              <X className="h-4 w-4 text-plum" />
            </button>
          </div>
          <div className="p-6">
            <nav className="flex flex-col gap-1">
              <MobileNavLink to="/shop" onClick={() => setMobileNavOpen(false)}>Shop all</MobileNavLink>
              <MobileNavLink to="/shop?featured=1" onClick={() => setMobileNavOpen(false)}>Featured</MobileNavLink>
              <MobileNavLink to="/shop?new=1" onClick={() => setMobileNavOpen(false)}>New releases</MobileNavLink>
              <MobileNavLink to="/shop?bestseller=1" onClick={() => setMobileNavOpen(false)}>Best sellers</MobileNavLink>
              <MobileNavLink to="/about" onClick={() => setMobileNavOpen(false)}>About</MobileNavLink>
            </nav>
            <div className="mt-10">
              <div className="text-[11px] uppercase tracking-widest text-plum/60 mb-3">Categories</div>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORY_DECK.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/shop?category=${c.slug}`}
                    onClick={() => setMobileNavOpen(false)}
                    className="text-sm text-plum-deep py-2 border-b border-brand"
                    data-testid={`mobile-cat-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}

function MobileNavLink({ to, onClick, children }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="font-serif-display text-2xl text-plum-deep py-3 border-b border-brand"
    >
      {children}
    </NavLink>
  );
}
